import { useMutation, useReactiveVar } from "@apollo/client";
import { DotsHorizontalIcon } from "@heroicons/react/outline";
import {
  ControlledMenu,
  Menu,
  MenuDivider,
  MenuItem,
  SubMenu,
} from "@szhsin/react-menu";
import moment from "moment";
import { ReactNode, useCallback, useState } from "react";
import {
  DeleteDraftMeetingNewPageMutationMutation,
  DeleteDraftMeetingNewPageMutationMutationVariables,
  MeetingDialogFragmentFragment,
  MeetingViewMeetingNodeNewPageFragmentFragment,
  OnlineVideoConference,
  PermissionNode,
  SubmitOneononeNewPageMutation,
  SubmitOneononeNewPageMutationVariables,
  UpdateMeetingFinalizationStatusNewPageMutationMutation,
  UpdateMeetingFinalizationStatusNewPageMutationMutationVariables,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import MeetingDialog from "@apps/meeting-dialog/meeting-dialog";
import { meetingDialogAction } from "@apps/meeting-dialog/meeting-dialog-constants";
import submitOneononeMutation from "@apps/meeting-new/components/prep/graphql/submit-oneonone-mutation";
import updateMeetingFinalizationStatusMutation from "@apps/meeting-new/components/prep/graphql/update-meeting-finalization-status-mutation";
import deleteDraftMeetingMutation from "@apps/meeting-new/graphql/delete-draft-meeting-mutation";
import getMeetingQuery from "@apps/meeting-new/graphql/get-meeting-query";
import { meetingsShowingEmptyCardsVar } from "@apps/profile/oneonone-tab/oneonone-empty-cards-state";
import SetAgendaAsTemplateDialog from "@apps/set-agenda-as-template-dialog/set-agenda-as-template-dialog";
import Templates from "@apps/templates/dialog";
import { currentUserVar, successNotificationVar } from "@cache/cache";
import Button, { ButtonProps } from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import { useLink } from "@components/link/link-helpers";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { getUrl } from "@helpers/helpers";

import { getOptimisticFinalizedAgenda } from "./agenda-finalization";
import CalendarEventMenu from "./components/calendar-event-menu";
import CopyUrlMenu from "./components/copy-url-menu";
import CreateTemplateDialog from "./components/create-template-dialog";
import IgnoreMenu from "./components/ignore-menu";
import MeetingDetailsModal from "./components/meeting-details-modal";

const iconClass = "mr-3 h-4 w-4 text-gray-400";

const MeetingDropdownMenu = ({
  meeting,
  meetingGroup,
  isContextMenu = false,
  buttonClassName = "",
  buttonProps = {},
  anchorPoint,
  onToggleMenu,
  onMenuChange,
  onOpenSendNotesDialog,
  onMeetingUpdated,
  children,
  ...menuProps
}: {
  buttonProps?: ButtonProps;
  meeting: {
    id: number;
    title?: string | null;
    organizer?: BasicUser | null;
    isRecurring: boolean;
    isFinalized?: boolean;
    canFinalize?: Pick<PermissionNode, "permission"> | null;
    hasIncompleteMandatoryTopics?: boolean;
    externalUrl?: string | null;
    agendaTopics?: MeetingViewMeetingNodeNewPageFragmentFragment["agendaTopics"];
    videoConferenceUrl?: string | null;
    draft: boolean;
    videoConferenceType?: OnlineVideoConference | null;
    transcript?: {
      id: number;
    } | null;
    canDelete?: Pick<PermissionNode, "permission"> | null;
    canUpdate?: Pick<PermissionNode, "permission"> | null;
    ignored: boolean;
    hasBotInMeeting: boolean;
    botIsRecording: boolean;
    botIsWaitingToJoinMeeting: boolean;
  };
  meetingGroup: {
    id: number;
    isFormalOneonone: boolean;
    facilitator?: BasicUser | null;
    hasTemplate?: boolean | null;
    addTranscriptionBotToMeetingsByDefault: boolean;
    transcribeMeetingsByDefault?: boolean | null;
  };
  children?: ReactNode;
  size?: number | string;
  className?: string;
  buttonClassName?: string;
  isContextMenu?: boolean;
  anchorPoint?: any;
  onToggleMenu?: (opened: boolean) => void;
  onMenuChange?: ({ open }: { open: boolean }) => void;
  onOpenSendNotesDialog?: () => void;
  onMeetingUpdated?: (data: { meeting: MeetingDialogFragmentFragment }) => void;
}) => {
  const [viewMeetingDetailsModal, setViewMeetingDetailsModal] = useState(false);
  const currentUser = currentUserVar();
  const meetingsShowingEmptyCards = useReactiveVar(
    meetingsShowingEmptyCardsVar,
  );
  const isShowingEmptyCards = meetingsShowingEmptyCards.has(meeting.id);
  const link = useLink();

  const [openCreateTemplateDialog, setOpenCreateTemplateDialog] =
    useState(false);
  const [openTemplateDialog, setOpenTemplatesDialog] = useState(false);
  const [openSetAgendaAsTemplateDialog, setOpenSetAgendaAsTemplateDialog] =
    useState(false);
  const [isShowingMeetingDialogAction, setIsShowingMeetingDialogAction] =
    useState<meetingDialogAction | false>(false);
  const [deleteDraftMeeting] = useMutation<
    DeleteDraftMeetingNewPageMutationMutation,
    DeleteDraftMeetingNewPageMutationMutationVariables
  >(deleteDraftMeetingMutation);

  const handleOpenTemplatesDialog = () => {
    setOpenTemplatesDialog(true);
  };
  const handleCreateTemplateDialog = () => {
    setOpenCreateTemplateDialog(true);
  };

  const handleSetAgendaAsTemplate = () => {
    setOpenSetAgendaAsTemplateDialog(true);
  };

  const handleClickDeleteDraftMeeting = useCallback(() => {
    deleteDraftMeeting({
      variables: {
        meetingId: meeting.id,
      },
      update: (cache) => {
        const cacheId = cache.identify(meeting);
        cache.evict({ id: cacheId });
        cache.gc();
      },
      onCompleted: () => {
        successNotificationVar({ title: "Meeting discarded." });
        link.redirect("/");
      },
    });
  }, [deleteDraftMeeting, meeting, link]);

  const meetingUrl = getUrl({
    meetingGroupId: meetingGroup.id,
    meetingId: meeting.id,
  });
  const handleClickEditMeetingSettings = () => {
    link.redirect(
      `${getUrl({
        meetingGroupId: meetingGroup.id,
      })}/settings?from=${meetingUrl}`,
    );
  };

  const topics =
    meeting.agendaTopics?.map((occurrence) => occurrence.topic) ?? [];
  const mandatoryTopics = topics.filter((topic) => topic.isMandatory);
  const mandatoryTopicsWithHiddenNotes = mandatoryTopics.filter((node) => {
    const hiddenIndividualNotes =
      node.individualNotes?.edges.filter(
        (edge) => edge?.node?.creator.id === currentUser.id && edge.node.hidden,
      ) || [];
    return hiddenIndividualNotes.length > 0;
  });
  const hasMandatoryTopicsWithHiddenNotes =
    mandatoryTopicsWithHiddenNotes.length > 0;
  const errorMatches = [
    {
      match: "Cannot finalize a meeting with incomplete mandatory topics",
      title: "In order to be finalized, all topics must contain notes.",
    },
  ];

  const [toggleFinalizeMeeting, { loading: loadingFinalize }] = useMutation<
    UpdateMeetingFinalizationStatusNewPageMutationMutation,
    UpdateMeetingFinalizationStatusNewPageMutationMutationVariables
  >(updateMeetingFinalizationStatusMutation);

  const handleSaveFinalizeMeeting = (newIsFinalized: boolean) => {
    if (!meeting.agendaTopics) {
      return;
    }
    toggleFinalizeMeeting({
      variables: {
        meetingId: meeting.id,
        isFinalized: newIsFinalized,
      },
      context: { errorMatches },
      optimisticResponse: {
        __typename: "Mutation",
        updateMeetingFinalizationStatus: {
          meeting: {
            ...meeting,
            __typename: "MeetingNode",
            isFinalized: newIsFinalized,
            finalizedAt: newIsFinalized ? moment().format() : null,
            finalizedBy: newIsFinalized ? currentUser : null,
            agendaTopics: getOptimisticFinalizedAgenda(
              meeting.agendaTopics,
              newIsFinalized,
            ),
          },
          __typename: "UpdateMeetingFinalizationStatus",
        },
      },
    });
  };

  const handleClickFinalizeMeeting = () => handleSaveFinalizeMeeting(true);

  const [submitOneonone, { loading: loadingSubmit }] = useMutation<
    SubmitOneononeNewPageMutation,
    SubmitOneononeNewPageMutationVariables
  >(submitOneononeMutation);

  const handleClickSubmit = () => {
    submitOneonone({
      variables: {
        meetingId: meeting.id,
        unhideIndividualNotes: true,
      },
    });
  };

  const organizerId = meeting.organizer?.id;

  const isCurrentUserTheOrganizer = organizerId === currentUser.id;

  const canShowFinalize =
    !!meetingGroup.isFormalOneonone &&
    !meeting.isFinalized &&
    !!meeting.canFinalize?.permission;
  const finalizeBlockedByMandatoryTopics =
    canShowFinalize && !!meeting.hasIncompleteMandatoryTopics;

  const items = (
    <>
      {children}
      <>
        <MenuItem
          className={"text-sm flex items-center"}
          onClick={() => setViewMeetingDetailsModal(true)}
        >
          <TopicflowIcon type="info" className={iconClass} />
          View Meeting Details
        </MenuItem>
        {meeting.externalUrl && (
          <CalendarEventMenu externalUrl={meeting.externalUrl} />
        )}
        <MenuDivider />
        {meeting.draft && meeting.canUpdate?.permission && (
          <>
            <MenuItem
              className={"text-sm flex items-center"}
              onClick={() =>
                setIsShowingMeetingDialogAction(meetingDialogAction.publish)
              }
            >
              <TopicflowIcon type="paperAirplane" className={iconClass} />
              Publish Meeting
            </MenuItem>
            {/* Hide Discard while the get-started empty-state cards are
                visible on this meeting — discarding one would bounce the user
                back to an empty 1-on-1 tab and immediately auto-create
                another draft. Once the user picks one of the cards (cards
                unmount, drop out of meetingsShowingEmptyCardsVar), Discard
                becomes available again. */}
            {!isShowingEmptyCards && (
              <MenuItem
                className={"text-sm flex items-center"}
                onClick={handleClickDeleteDraftMeeting}
              >
                <TopicflowIcon type="trash" className={iconClass} />
                Discard Draft Meeting
              </MenuItem>
            )}
            <MenuDivider />
          </>
        )}

        {isCurrentUserTheOrganizer && (
          <>
            <MenuItem
              className={"text-sm flex items-center"}
              onClick={() =>
                setIsShowingMeetingDialogAction(meetingDialogAction.edit)
              }
              disabled={!isCurrentUserTheOrganizer}
            >
              <TopicflowIcon type="edit" className={iconClass} />
              Edit Meeting
            </MenuItem>
            <MenuDivider />
          </>
        )}
      </>

      {!isContextMenu && meeting && (
        <>
          <SubMenu
            className="text-sm"
            label={
              <div className="flex items-center text-sm">
                <TopicflowIcon type="layersTwo" className={iconClass} />
                Templates
              </div>
            }
          >
            <MenuItem
              className="text-sm"
              onClick={handleClickEditMeetingSettings}
            >
              Edit Meeting Template
            </MenuItem>
            <MenuItem className="text-sm" onClick={handleOpenTemplatesDialog}>
              Apply a Template...
            </MenuItem>
            <MenuItem
              className="text-sm block"
              onClick={handleCreateTemplateDialog}
            >
              Save Agenda to Template Library
            </MenuItem>
            {meeting.isRecurring && (
              <MenuItem
                className="text-sm block"
                onClick={handleSetAgendaAsTemplate}
              >
                Reuse this Agenda in Upcoming Meetings
              </MenuItem>
            )}
            <MenuDivider />
            <MenuItem
              className="text-sm"
              onClick={() => link.redirect("/templates")}
            >
              View All Templates
            </MenuItem>
          </SubMenu>
          <MenuDivider />
        </>
      )}

      {!isContextMenu && meeting && (
        <>
          <CopyUrlMenu meeting={meeting} meetingGroup={meetingGroup} />
          <MenuItem
            className={"text-sm flex items-center"}
            onClick={onOpenSendNotesDialog}
          >
            <TopicflowIcon type="share" className={iconClass} />
            Share Notes
          </MenuItem>
          <MenuDivider />
        </>
      )}

      <SubMenu
        label={
          <div className="flex items-center text-sm">
            <TopicflowIcon type="horizontalDots" className={iconClass} />
            More...
          </div>
        }
        className="text-sm"
      >
        {meeting.canUpdate?.permission && (
          <MenuItem
            className={"text-sm flex items-center"}
            onClick={handleClickEditMeetingSettings}
          >
            <TopicflowIcon type="today" className={iconClass} />
            Slack Notifications
          </MenuItem>
        )}
        {finalizeBlockedByMandatoryTopics ? (
          <SubMenu
            label={
              <div className="flex items-center text-sm">
                <TopicflowIcon type="checkCircle" className={iconClass} />
                Finalize Meeting
              </div>
            }
            className={"text-sm"}
          >
            <MenuItem className={"text-sm"} disabled>
              <div className="flex flex-col">
                <div className="text-gray-700 font-semibold">
                  Mandatory Topics are Incomplete
                </div>
                <div className="text-gray-500 max-w-64 gap-4">
                  You must add notes to all the topics with a{" "}
                  <span className="text-xs bg-amber-100 text-amber-700 px-1 py-0.5">
                    Mandatory
                  </span>{" "}
                  tag, and make sure that they are not set to "Make private".
                </div>
              </div>
            </MenuItem>
            <MenuDivider />
            <MenuItem
              className={"text-sm"}
              onClick={handleClickSubmit}
              disabled={loadingSubmit}
            >
              Reveal my notes to all participants
            </MenuItem>
          </SubMenu>
        ) : canShowFinalize ? (
          <MenuItem
            disabled={loadingFinalize || hasMandatoryTopicsWithHiddenNotes}
            className={"text-sm flex items-center"}
            onClick={handleClickFinalizeMeeting}
          >
            <TopicflowIcon type="checkCircle" className={iconClass} />
            <div className="flex flex-col items-start">
              <div className="text-left">Finalize</div>
              {hasMandatoryTopicsWithHiddenNotes && (
                <div className="text-xs text-gray-500">
                  You must reveal all your notes in order to finalize
                </div>
              )}
            </div>
          </MenuItem>
        ) : null}
        <IgnoreMenu meeting={meeting} meetingGroup={meetingGroup} />
        <MenuDivider />
        <MenuItem className={"text-sm flex items-center"}>
          <TopicflowIcon type="text" className={iconClass} />
          <a
            href={`${getUrl({
              meetingGroupId: meetingGroup.id,
              meetingId: meeting.id,
            })}.md`}
            target="_blank"
            rel="noreferrer"
          >
            Markdown
          </a>
        </MenuItem>
      </SubMenu>
    </>
  );

  // Render
  return (
    <>
      {meeting && viewMeetingDetailsModal && (
        <MeetingDetailsModal
          meetingId={meeting.id}
          onClose={() => {
            setViewMeetingDetailsModal(false);
          }}
        />
      )}
      {meeting && meetingGroup && openTemplateDialog && (
        <Templates
          meeting={meeting}
          meetingGroup={meetingGroup}
          onClose={() => setOpenTemplatesDialog(false)}
        />
      )}
      {openCreateTemplateDialog && (
        <CreateTemplateDialog
          meeting={meeting}
          onClose={() => setOpenCreateTemplateDialog(false)}
        />
      )}
      {openSetAgendaAsTemplateDialog && (
        <SetAgendaAsTemplateDialog
          meeting={meeting}
          meetingGroup={meetingGroup}
          onClose={() => setOpenSetAgendaAsTemplateDialog(false)}
        />
      )}

      {isShowingMeetingDialogAction && (
        <MeetingDialog
          meetingId={meeting.id}
          meetingGroupId={meetingGroup.id}
          onClose={() => setIsShowingMeetingDialogAction(false)}
          defaultAction={isShowingMeetingDialogAction}
          onSaved={(data) => {
            if (onMeetingUpdated) {
              onMeetingUpdated(data);
            }
            setIsShowingMeetingDialogAction(false);
          }}
          refetchQueries={[getMeetingQuery]}
        />
      )}
      {isContextMenu && onToggleMenu ? (
        <ControlledMenu
          {...menuProps}
          portal={true}
          className="z-dropdown"
          anchorPoint={anchorPoint}
          onClose={() => onToggleMenu(false)}
        >
          {items}
        </ControlledMenu>
      ) : (
        <Menu
          onMenuChange={onMenuChange}
          className="z-dropdown"
          menuClassName="min-w-56"
          portal
          align="end"
          gap={8}
          menuButton={
            <Button
              theme={buttonTheme.iconGray}
              className={buttonClassName}
              icon={DotsHorizontalIcon}
              aria-label="Meeting dropdown button"
              {...buttonProps}
            />
          }
          aria-label="Meeting dropdown list"
        >
          {items}
        </Menu>
      )}
    </>
  );
};

export default MeetingDropdownMenu;
